import React, { useEffect, useState } from 'react';
import { saas } from '../lib/saasApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Search, ShieldCheck, Loader2, AlertTriangle, RefreshCw, UserX, MailPlus, History, Trash2, KeyRound, ArchiveX,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageLayout } from '../components/Sidebar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const RESET_LEVELS = [
  { key: 'session', label: 'Level 1 — Revoke sessions',
    description: 'Force the user to log in again. Preserves Person, User, Memberships, Data.',
    icon: KeyRound, danger: false },
  { key: 'membership', label: 'Level 2 — Revoke workspace membership',
    description: 'Removes their access to one workspace. Person and User remain.',
    icon: UserX, danger: false },
  { key: 'user_account', label: 'Level 3 — Reset auth account',
    description: 'Clears sessions and resets login linkage. Person remains.',
    icon: RefreshCw, danger: true },
  { key: 'person_full', label: 'Level 4 — Archive person',
    description: 'Archives Person, revokes all memberships, clears all sessions. Severe.',
    icon: ArchiveX, danger: true },
];


export default function AdminIdentityPage() {
  const [isAdmin, setIsAdmin] = useState(null);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [audit, setAudit] = useState([]);
  const [resetDialog, setResetDialog] = useState({ open: false, level: null });
  const [resetReason, setResetReason] = useState('');
  const [resetOrgId, setResetOrgId] = useState('');
  const [resetting, setResetting] = useState(false);
  const [reinviteDialog, setReinviteDialog] = useState({ open: false });
  const [reinviteOrgId, setReinviteOrgId] = useState('');
  const [reinviteRole, setReinviteRole] = useState('Member');
  const [reinviting, setReinviting] = useState(false);

  useEffect(() => {
    fetch('/api/testing-lab/super-admin/me', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setIsAdmin(!!d.is_super_admin))
      .catch(() => setIsAdmin(false));
  }, []);

  const loadAudit = async () => {
    try {
      const a = await saas.identityAudit(50);
      setAudit(a.items || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (isAdmin) loadAudit();
  }, [isAdmin]);

  const doSearch = async () => {
    if (!search.trim()) return;
    setSearching(true);
    setSnapshot(null);
    try {
      const params = search.includes('@') ? { email: search.trim() } :
                     search.startsWith('person_') ? { person_id: search.trim() } :
                     { user_id: search.trim() };
      const result = await saas.identityInspect(params);
      setSnapshot(result);
      if (!result.found) {
        toast.error('No identity found for that search');
      }
    } catch (e) {
      toast.error('Inspect failed: ' + (e.detail || e.message));
    } finally {
      setSearching(false);
    }
  };

  const openResetDialog = (level) => {
    setResetDialog({ open: true, level });
    setResetReason('');
    setResetOrgId(snapshot?.memberships?.[0]?.org_id || '');
  };

  const submitReset = async () => {
    if (!snapshot?.found) return;
    setResetting(true);
    try {
      const body = {
        target_email: snapshot.user?.email || snapshot.person?.primary_email,
        target_user_id: snapshot.user?.user_id,
        target_person_id: snapshot.person?.person_id,
        level: resetDialog.level,
        reason: resetReason || null,
      };
      if (resetDialog.level === 'membership') {
        if (!resetOrgId) {
          toast.error('Pick an org to revoke membership for');
          setResetting(false);
          return;
        }
        body.org_id = resetOrgId;
      }
      const result = await saas.identityReset(body);
      toast.success(`Reset complete: ${JSON.stringify(result.summary)}`);
      setResetDialog({ open: false, level: null });
      await doSearch();
      await loadAudit();
    } catch (e) {
      toast.error('Reset failed: ' + (e.detail || e.message));
    } finally {
      setResetting(false);
    }
  };

  const submitReinvite = async () => {
    if (!reinviteOrgId) {
      toast.error('Pick an org first');
      return;
    }
    setReinviting(true);
    try {
      const body = {
        target_email: snapshot.user?.email || snapshot.person?.primary_email,
        target_person_id: snapshot.person?.person_id,
        org_id: reinviteOrgId,
        role: reinviteRole,
      };
      const result = await saas.identityReinvite(body);
      toast.success(`Reinvite created — invite_id ${result.reinvite.invite_id}`);
      setReinviteDialog({ open: false });
      await doSearch();
      await loadAudit();
    } catch (e) {
      toast.error('Reinvite failed: ' + (e.detail || e.message));
    } finally {
      setReinviting(false);
    }
  };

  if (isAdmin === null) {
    return <PageLayout><div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div></PageLayout>;
  }
  if (!isAdmin) {
    return (
      <PageLayout>
        <div className="max-w-md mx-auto p-12 text-center">
          <ShieldCheck className="w-12 h-12 mx-auto text-slate-400" />
          <h1 className="text-2xl font-bold mt-4">Super Admin only</h1>
          <p className="text-slate-500 mt-2">You don't have access to the Identity Admin console.</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div data-testid="admin-identity-page" className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 text-violet-500" />
          <div>
            <h1 className="text-3xl font-bold">Identity Admin</h1>
            <p className="text-slate-500 text-sm">Inspect, reset, and reinvite people without DB surgery.</p>
          </div>
        </div>

        <Tabs defaultValue="search" className="w-full">
          <TabsList>
            <TabsTrigger data-testid="tab-search" value="search">Search & Reset</TabsTrigger>
            <TabsTrigger data-testid="tab-audit" value="audit">Audit Log</TabsTrigger>
          </TabsList>

          <TabsContent value="search" className="space-y-4 pt-2">
            <Card>
              <CardHeader>
                <CardTitle>Find identity</CardTitle>
                <CardDescription>Search by email, user_id, or person_id.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    data-testid="identity-search-input"
                    placeholder="email@example.com  or  user_xxxxxx  or  person_xxxxxx"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                  />
                  <Button data-testid="identity-search-btn" onClick={doSearch} disabled={searching}>
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {snapshot && snapshot.found === false && (
              <Card className="border-amber-300">
                <CardContent className="pt-6 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <span>No matching identity found.</span>
                </CardContent>
              </Card>
            )}

            {snapshot?.found && (
              <Card data-testid="identity-snapshot">
                <CardHeader>
                  <CardTitle>{snapshot.person?.full_name || snapshot.user?.name || '(unnamed)'}</CardTitle>
                  <CardDescription>
                    {snapshot.user?.email || snapshot.person?.primary_email}
                    {snapshot.user?.has_authenticated && <Badge className="ml-2" variant="outline">authenticated</Badge>}
                    {snapshot.person?.status === 'archived' && <Badge className="ml-2" variant="destructive">archived</Badge>}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-xs text-slate-500">person_id</div>
                      <div className="font-mono text-xs truncate">{snapshot.person?.person_id || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">user_id</div>
                      <div className="font-mono text-xs truncate">{snapshot.user?.user_id || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Active sessions</div>
                      <div className="font-mono text-xs">{snapshot.active_sessions_count}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Subscription</div>
                      <div className="font-mono text-xs">{snapshot.subscription?.plan_key || 'free_personal'}</div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-medium mb-2">Memberships ({snapshot.memberships?.length || 0})</h3>
                    {snapshot.memberships?.length ? (
                      <div className="border rounded">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 dark:bg-slate-900">
                            <tr><th className="text-left p-2">Org</th><th className="text-left p-2">Role</th><th className="text-left p-2">Status</th></tr>
                          </thead>
                          <tbody>
                            {snapshot.memberships.map(m => {
                              const org = snapshot.organizations.find(o => o.org_id === m.org_id);
                              return (
                                <tr key={m.membership_id} className="border-t">
                                  <td className="p-2">{org?.org_name || org?.name || m.org_id}</td>
                                  <td className="p-2">{m.role}</td>
                                  <td className="p-2"><Badge variant={m.status === 'active' ? 'outline' : 'secondary'}>{m.status}</Badge></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : <div className="text-sm text-slate-500">No memberships.</div>}
                  </div>

                  <div>
                    <h3 className="font-medium mb-2">Invitations ({snapshot.invitations?.length || 0})</h3>
                    {snapshot.invitations?.length ? (
                      <div className="border rounded text-sm">
                        {snapshot.invitations.slice(0, 5).map(i => (
                          <div key={i.invite_id} className="p-2 border-b last:border-b-0 flex items-center justify-between">
                            <span>{i.org_name || i.org_id}</span>
                            <Badge variant={i.status === 'pending' ? 'default' : 'secondary'}>{i.status}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : <div className="text-sm text-slate-500">No invitations.</div>}
                  </div>

                  {/* Action panel */}
                  <div className="border-t pt-4">
                    <h3 className="font-medium mb-3">Actions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {RESET_LEVELS.map((level) => {
                        const Icon = level.icon;
                        return (
                          <Button
                            key={level.key}
                            data-testid={`reset-${level.key}-btn`}
                            variant={level.danger ? 'destructive' : 'outline'}
                            className="justify-start h-auto py-3"
                            onClick={() => openResetDialog(level.key)}
                          >
                            <Icon className="w-4 h-4 mr-3 flex-shrink-0" />
                            <div className="text-left">
                              <div className="font-medium text-sm">{level.label}</div>
                              <div className="text-xs opacity-70">{level.description}</div>
                            </div>
                          </Button>
                        );
                      })}
                      <Button
                        data-testid="reinvite-btn"
                        className="justify-start h-auto py-3 col-span-1 md:col-span-2 bg-violet-600 hover:bg-violet-700 text-white"
                        onClick={() => {
                          setReinviteDialog({ open: true });
                          setReinviteOrgId(snapshot.memberships?.[0]?.org_id || '');
                        }}
                      >
                        <MailPlus className="w-4 h-4 mr-3" />
                        <div className="text-left">
                          <div className="font-medium text-sm">Reinvite to workspace</div>
                          <div className="text-xs opacity-90">Send a fresh invite — supersedes any prior pending invite.</div>
                        </div>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="audit" className="pt-2">
            <Card data-testid="audit-card">
              <CardHeader>
                <CardTitle>Audit Log</CardTitle>
                <CardDescription>All identity reset/reinvite actions across this org.</CardDescription>
              </CardHeader>
              <CardContent>
                {audit.length === 0 ? (
                  <div className="text-sm text-slate-500 py-8 text-center">No audit entries yet.</div>
                ) : (
                  <div className="space-y-1 max-h-[600px] overflow-auto">
                    {audit.map((a) => (
                      <div key={a.audit_id} className="border rounded p-3 text-sm">
                        <div className="flex justify-between items-center mb-1">
                          <Badge variant="outline">{a.level}</Badge>
                          <span className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-slate-500">Actor:</span> {a.actor_email || a.actor_user_id || 'system'}
                          {' → '}
                          <span className="text-slate-500">Target:</span> {a.target_email || a.target_person_id || a.target_user_id || '—'}
                        </div>
                        {a.reason && <div className="text-xs text-slate-500 mt-1">Reason: {a.reason}</div>}
                        <pre className="text-xs bg-slate-50 dark:bg-slate-900 mt-2 p-2 rounded overflow-auto">{JSON.stringify(a.summary, null, 2)}</pre>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Reset Dialog */}
        <Dialog open={resetDialog.open} onOpenChange={(o) => !o && setResetDialog({ open: false, level: null })}>
          <DialogContent data-testid="reset-dialog">
            <DialogHeader>
              <DialogTitle>{RESET_LEVELS.find(l => l.key === resetDialog.level)?.label}</DialogTitle>
              <DialogDescription>{RESET_LEVELS.find(l => l.key === resetDialog.level)?.description}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {resetDialog.level === 'membership' && (
                <div>
                  <Label>Workspace</Label>
                  <Select value={resetOrgId} onValueChange={setResetOrgId}>
                    <SelectTrigger><SelectValue placeholder="Pick a workspace" /></SelectTrigger>
                    <SelectContent>
                      {(snapshot?.organizations || []).map(o => (
                        <SelectItem key={o.org_id} value={o.org_id}>{o.org_name || o.name || o.org_id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Reason (audited)</Label>
                <Textarea
                  data-testid="reset-reason-input"
                  value={resetReason}
                  onChange={(e) => setResetReason(e.target.value)}
                  placeholder="e.g. Password compromised — user requested reset"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setResetDialog({ open: false, level: null })}>Cancel</Button>
              <Button data-testid="reset-confirm-btn" onClick={submitReset} disabled={resetting} variant={RESET_LEVELS.find(l => l.key === resetDialog.level)?.danger ? 'destructive' : 'default'}>
                {resetting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Confirm Reset
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reinvite Dialog */}
        <Dialog open={reinviteDialog.open} onOpenChange={(o) => !o && setReinviteDialog({ open: false })}>
          <DialogContent data-testid="reinvite-dialog">
            <DialogHeader>
              <DialogTitle>Reinvite to workspace</DialogTitle>
              <DialogDescription>
                Generates a fresh invite token. Any pending invite for the same person + workspace is superseded.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Workspace</Label>
                <Select value={reinviteOrgId} onValueChange={setReinviteOrgId}>
                  <SelectTrigger><SelectValue placeholder="Pick a workspace" /></SelectTrigger>
                  <SelectContent>
                    {(snapshot?.organizations || []).map(o => (
                      <SelectItem key={o.org_id} value={o.org_id}>{o.org_name || o.name || o.org_id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={reinviteRole} onValueChange={setReinviteRole}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Member">Member</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Owner">Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setReinviteDialog({ open: false })}>Cancel</Button>
              <Button data-testid="reinvite-confirm-btn" onClick={submitReinvite} disabled={reinviting}>
                {reinviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MailPlus className="w-4 h-4 mr-2" />}
                Send Invite
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageLayout>
  );
}
